import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { ProfilesView, ProfilesViewProps } from './Profiles.view';
import { ContainerListItem } from 'components/actions/ListItem/ListItem.view';
import {
  AppInstalledLazyQueryHookResult,
  ConnectMainSocialProfileMutationFn,
  ExternalSecondaryProfilesLazyQueryHookResult,
  GetSocialProfilesDocument,
  GetSocialProfilesLazyQueryHookResult,
  GetSocialProfilesQuery,
  GetSocialProfilesQueryVariables,
  InstagramApiType,
  PlanUsageType,
  Network,
  ReconnectSocialProfileMutationFn,
  SecondaryProfileType,
  SocialAuthTokenLazyQueryHookResult,
  SocialAuthTokenQuery,
  SocialOAuthContext,
  SocialOAuthProvider,
  SocialProfileFields_InstagramSocialProfile_Fragment,
  SocialProfileFieldsFragment,
  StartSocialOAuthMutationFn,
  UsageFieldsFragmentDoc
} from '../../../models/graphql';
import { ApolloError, useApolloClient, useReactiveVar } from '@apollo/client';
import { AuthContextEnum } from '../../../constants/auth';
import { ISocialOAuth } from '../../../models/auth';
import { showToast } from '../../../helpers/showToast';
import { capitalize } from 'lodash';
import { GENERIC_ERROR_MESSAGE, getApolloError } from '../../../constants/apollo-errors';
import { ApolloErrorMapKeysType, ApolloErrorMapTypesType } from '../../../models/apollo-errors';
import { getApolloErrorMessage, remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { profilesHelpers } from './Profiles.helpers';
import { InfoModalObject } from './InfoModal/InfoModal.view';
import { MappedProfileType } from 'components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import {
  ConnectMainSocialProfile_connectMainSocialProfile,
  ExternalSecondaryProfiles_externalSecondaryProfiles,
  GetMe_me_organizations,
  GetSocialProfiles_workspace_socialProfiles,
  GetSocialProfiles_workspace_socialProfiles_InstagramSocialProfile,
  ReconnectSocialProfile_reconnectSocialProfile,
  ReconnectSocialProfile_reconnectSocialProfile_socialProfile,
  ReconnectSocialProfile_reconnectSocialProfile_socialProfile_FacebookSocialProfile,
  SocialAuthToken_socialAuthToken
} from '../../../models/legacy-graphql';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { AppContext, socialOAuthVar } from '../../../providers/AppProvider';
import { OnboardingContext } from '../../Onboarding/states/OnboardingProvider';
import { useUserData } from '../../../hooks/useUserData';
import { ContainerListItemExtended } from '../../../components/actions/ButtonMenu/ButtonMenu.view';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { OnboardingStaticContent } from '../../Onboarding/constants/content';

export type ProfilesControllerProps = Omit<
  ProfilesViewProps,
  | 'cancelConnect'
  | 'changeWhoPostsModal'
  | 'connectFacebookItems'
  | 'connectLinkedinItems'
  | 'connectProfile'
  | 'deleteProfile'
  | 'directPublishingModal'
  | 'errorModal'
  | 'getListActionItems'
  | 'getSocialProfile'
  | 'infoModal'
  | 'reconnectProfile'
  | 'reconnectProfileAfterOAuth'
  | 'removeProfileModal'
  | 'secondaryProfilesModal'
  | 'setChangeWhoPostsModal'
  | 'setDirectPublishing'
  | 'setDirectPublishingModal'
  | 'setErrorModal'
  | 'setInfoModal'
  | 'setLoading'
  | 'setRemoveProfileModal'
  | 'setSecondaryProfilesModal'
  | 'hasMobileAppConnected'
  | 'showIgConnectModal'
  | 'setShowIgConnectModal'
> & {
  // extend your ProfilesViewProps
  appInstalledQuery: AppInstalledLazyQueryHookResult[0];
  connectMainSocialProfile: ConnectMainSocialProfileMutationFn;
  errorForRemapping: ApolloError | null;
  getSecondaryProfiles: ExternalSecondaryProfilesLazyQueryHookResult[0];
  getSocialProfileQuery: GetSocialProfilesLazyQueryHookResult[0];
  socialAuthTokenQuery: SocialAuthTokenLazyQueryHookResult[0];
  reconnectSocialProfileMutation: ReconnectSocialProfileMutationFn;
  // upgradeInstagramSocialProfileMutation: UpgradeInstagramSocialProfileMutationFn;
};

export type ConnectProfileErrorModals =
  | 'alreadyConnected'
  | 'reconnectedAnother'
  | 'reconnectedFBGroup'
  | 'upgradeIgProfile'
  | SecondaryProfileType;

export type ErrorModalType = {
  type: ConnectProfileErrorModals;
  oAuthProvider: SocialOAuthProvider;
  network: Network;
  content: JSX.Element;
  profile: GetSocialProfiles_workspace_socialProfiles | null;
  socialOAuthToken?: string;
  socialOAuthTokenResult?: SocialAuthTokenQuery['socialAuthToken'];
};

export type InfoModalType = 'GroupAdded';

export type SecondaryProfilesModalType = {
  type: SecondaryProfileType;
  organization: GetMe_me_organizations;
  workspaceId: string;
  profiles: ExternalSecondaryProfiles_externalSecondaryProfiles[];
  socialOAuth: ISocialOAuth;
};

export type RemoveProfileConfirmationType = {
  show: boolean;
  profileId: string;
  profileName: string;
  typename?: GetSocialProfiles_workspace_socialProfiles['__typename'];
};

// @TODO: `profileNetwork` should be `simply network`
export type ConnectProfileOAuthType = {
  setLoading?: Dispatch<SetStateAction<boolean>>;
  oAuthProvider: SocialOAuthProvider;
  profileNetwork?: Network;
  context: AuthContextEnum;
  startSocialOAuth: StartSocialOAuthMutationFn;
  profile?: MappedProfileType;
  socialOAuthContext: SocialOAuthContext;
};

// @TODO: `profileNetwork` should be `simply network`
export type ConnectProfileOAuthModalType = {
  oAuthProvider: SocialOAuthProvider;
  profileNetwork?: Network;
  context: AuthContextEnum;
  profile?: GetSocialProfiles_workspace_socialProfiles;
  socialOAuthContext?: SocialOAuthContext;
};

export type FacebookReconnectType = {
  authTokenResult: SocialAuthToken_socialAuthToken | null;
  socialProfile: ReconnectSocialProfile_reconnectSocialProfile_socialProfile_FacebookSocialProfile | null;
  socialOAuth: ISocialOAuth | null;
};

export type FacebookReconnectAction =
  | { type: 'queryToken'; payload: FacebookReconnectType['authTokenResult'] }
  | {
      type: 'setSocialProfileAndOAuth';
      payload: {
        socialProfile: FacebookReconnectType['socialProfile'];
        socialOAuth: FacebookReconnectType['socialOAuth'];
      };
    }
  | { type: 'reset' };

export const FacebookReconnectInitialState: FacebookReconnectType = {
  authTokenResult: null,
  socialProfile: null,
  socialOAuth: null
};

export enum IgModalActionEnum {
  CONNECT = 'CONNECT',
  RECONNECT = 'RECONNECT'
}

export type IgModalType = {
  action: IgModalActionEnum;
  profile?: MappedProfileType;
};

export const ProfilesController = (props: ProfilesControllerProps): JSX.Element => {
  const { getSocialProfileQuery } = props;
  const { cache } = useApolloClient();
  const { userData, loading: loadingUser, error: errorUser } = useUserData();
  const { selectedWorkspaceId } = useContext(AppContext);
  const { showOnboarding, state } = useContext(OnboardingContext);
  const { version } = state;
  const disableProfile = showOnboarding && userData && OnboardingStaticContent[`v${version}`].disableDelete.profiles;
  const { selectedOrganization } = useSelectedOrganization();
  const { setLimitReachedModalState } = useContext(AppContext);
  const { canAddSocialProfile, getUsageFieldsLazyQuery } = useOrganizationPlan();
  const [loading, setLoading] = useState<boolean>(true);
  const [errorModal, setErrorModal] = useState<ErrorModalType | null>(null);
  const [infoModal, setInfoModal] = useState<InfoModalObject | null>(null);
  const [showIgConnectModal, setShowIgConnectModal] = useState<IgModalType | undefined>(undefined);
  const [secondaryProfilesModal, setSecondaryProfilesModal] = useState<SecondaryProfilesModalType | null>(null);
  const [removeProfileModal, setRemoveProfileModal] = useState<RemoveProfileConfirmationType>({
    show: false,
    profileId: '',
    profileName: ''
  });

  const [directPublishingModal, setDirectPublishingModal] =
    useState<SocialProfileFields_InstagramSocialProfile_Fragment | null>(null);
  const [profilesQueryCalled, setProfilesQueryCalled] = useState<boolean>(false);

  const connectLinkedinItems: ContainerListItem[] = [
    {
      action: () => {
        canAddSocialProfile(1)
          ? profilesHelpers.setNetworkAndCallOAuth({
              setLoading: setLoading,
              oAuthProvider: SocialOAuthProvider.LINKEDIN,
              profileNetwork: Network.LINKEDIN,
              context: AuthContextEnum.connectProfile,
              startSocialOAuth: props.startSocialOAuth,
              socialOAuthContext: SocialOAuthContext.CONNECT
            })
          : setLimitReachedModalState({ isOpen: true, type: 'profiles' });
      },
      label: 'Connect LinkedIn Profile'
    },
    {
      action: () => {
        canAddSocialProfile(1)
          ? profilesHelpers.setNetworkAndCallOAuth({
              setLoading: setLoading,
              oAuthProvider: SocialOAuthProvider.LINKEDIN,
              profileNetwork: Network.LINKEDIN,
              context: AuthContextEnum.connectCompany,
              startSocialOAuth: props.startSocialOAuth,
              socialOAuthContext: SocialOAuthContext.CONNECT
            })
          : setLimitReachedModalState({ isOpen: true, type: 'profiles' });
      },
      label: 'Connect LinkedIn Company'
    }
  ];
  const socialOAuth: ISocialOAuth | null = useReactiveVar(socialOAuthVar);

  const cancelConnect = () => {
    socialOAuthVar(null);
    setErrorModal(null);
  };

  // Creates the "Remove Profile" action item
  const createRemoveProfileItem = (
    profile: SocialProfileFieldsFragment
  ): ContainerListItem | ContainerListItemExtended => ({
    label: 'Remove Profile',
    action: () => {
      setRemoveProfileModal({
        show: true,
        profileId: profile.id,
        profileName: profile.displayName,
        typename: profile.__typename
      });
    },
    ...(profile.onboardingContent && disableProfile
      ? { disabled: true, disabledTooltipText: 'You will be able to delete these demo profiles later.' }
      : {})
  });

  // Returns reconnection action items if applicable
  const getReconnectionItems = (
    profile: SocialProfileFieldsFragment,
    oauth: SocialOAuthProvider | null,
    network: Network
  ): ContainerListItem[] => {
    // GENERIC SOCIAL PROFILES will never have oAuth type
    // TEMP occlusion for any IG but the Graph types
    if (
      !profile.connected ||
      !oauth ||
      (profile.__typename === 'InstagramSocialProfile' &&
        profile.apiType !== InstagramApiType.FACEBOOK_GRAPH &&
        profile.apiType !== InstagramApiType.INSTAGRAM_GRAPH)
    )
      return [];

    const action =
      network === Network.INSTAGRAM
        ? () => setShowIgConnectModal({ action: IgModalActionEnum.RECONNECT, profile })
        : () => reconnectProfile(profile, oauth, network);

    return [{ label: 'Re-connect', action }];
  };

  // Returns direct publishing toggle action items for Instagram profiles
  const getDirectPublishingItems = (profile: SocialProfileFieldsFragment): ContainerListItem[] => {
    if (profile.__typename !== 'InstagramSocialProfile') return [];

    if (profile.connected && profile.directPublishEnabled) {
      return [{ label: 'Disable Direct Publishing', action: () => setDirectPublishing(false, profile) }];
    }

    if (!profile.connected) {
      return [
        {
          label: profile.directPublishEnabled ? 'Disable Direct Publishing' : 'Enable Direct Publishing',
          action: () => setDirectPublishing(!profile.directPublishEnabled, profile)
        }
      ];
    }

    return [];
  };

  const getListActionItems = (profile: SocialProfileFieldsFragment): ContainerListItem[] => {
    const apiType = profile.__typename === 'InstagramSocialProfile' ? profile.apiType : undefined;
    const network = profilesHelpers.mapNetworkFromTypename(profile.__typename);
    const oauth = profilesHelpers.mapOAuthFromTypename(profile.__typename, apiType);

    if (!network) return [];

    return [
      ...getDirectPublishingItems(profile),
      ...getReconnectionItems(profile, oauth, network),
      createRemoveProfileItem(profile)
    ];
  };

  const setDirectPublishing = (
    status: boolean,
    profile: GetSocialProfiles_workspace_socialProfiles_InstagramSocialProfile,
    hideToast?: boolean
  ): void => {
    if (profile.apiType === InstagramApiType.FACEBOOK_GRAPH || profile.apiType === InstagramApiType.INSTAGRAM_GRAPH) {
      setLoading(true);
      props
        .updateDirectPublishing({
          variables: {
            socialProfileId: profile.id,
            enabled: status
          }
        })
        .then(result => {
          setLoading(false);
          if (result.data && !hideToast) {
            showToast({
              severity: 'success',
              summary: 'Profile Updated',
              detail: `Direct publishing ${status ? 'enabled' : 'disabled'} successfully.`
            });
          } else if (result.errors) {
            const message = result.errors.map((error, index) => (
              <p key={index}>
                {getApolloError(error.message as ApolloErrorMapKeysType, 'updateSocialProfileDirectPublishing')}
              </p>
            ));
            showToast({
              severity: 'error',
              summary: 'Profile Updated',
              detail: message,
              sticky: true
            });
          }
        })
        .catch(e => {
          showToast({
            severity: 'error',
            summary: 'Profile Updated',
            detail: remapApolloErrors(e),
            sticky: true
          });
        });
    } else if (profile.__typename === 'InstagramSocialProfile') {
      setDirectPublishingModal(profile);
    }
  };

  const reconnectProfile = (
    profile: SocialProfileFieldsFragment,
    oAuthProvider: SocialOAuthProvider,
    network: Network
  ): void => {
    profilesHelpers.setNetworkAndCallOAuth({
      setLoading: setLoading,
      startSocialOAuth: props.startSocialOAuth,
      context: AuthContextEnum.reconnectProfile,
      oAuthProvider: oAuthProvider,
      profileNetwork: network,
      profile,
      socialOAuthContext: SocialOAuthContext.RECONNECT
    });
  };

  const connectProfileAfterOAuth = async (socialOAuth: ISocialOAuth): Promise<void> => {
    if (socialOAuth.socialAuthToken) {
      const connectResult = await props
        .connectMainSocialProfile({
          variables: { socialAuthToken: socialOAuth.socialAuthToken, workspaceId: selectedWorkspaceId }
        })
        .finally(() => socialOAuthVar(null));

      if (connectResult.data) {
        connectResult.data.connectMainSocialProfile.forEach(
          (profile: ConnectMainSocialProfile_connectMainSocialProfile) => {
            const { connected, error, reconnected, additionalReconnection, socialProfile } = profile;
            if (additionalReconnection) {
              return;
            }

            if (connected && !error && !reconnected && socialProfile) {
              showToast({
                summary: `Profile Connected`,
                severity: 'success',
                detail: `Social profile '${socialProfile.displayName}' has been connected successfully.`
              });
              const newProfile = socialProfile;
              const socialProfilesCache = cache.readQuery<GetSocialProfilesQuery, GetSocialProfilesQueryVariables>({
                query: GetSocialProfilesDocument,
                variables: { workspaceId: selectedWorkspaceId }
              });

              socialProfilesCache &&
                cache.writeQuery<GetSocialProfilesQuery, GetSocialProfilesQueryVariables>({
                  query: GetSocialProfilesDocument,
                  variables: { workspaceId: selectedWorkspaceId },
                  data: {
                    ...socialProfilesCache,
                    workspace: {
                      ...socialProfilesCache.workspace,
                      socialProfiles: [...socialProfilesCache.workspace.socialProfiles, newProfile]
                    }
                  }
                });
              const oldUsage: PlanUsageType | null = cache.readFragment({
                fragment: UsageFieldsFragmentDoc,
                fragmentName: 'UsageFields',
                id: 'PlanUsageType:{}'
              });
              const newUsage = { ...oldUsage, socialProfiles: (oldUsage?.socialProfiles ?? 0) + 1 };
              cache.writeFragment({
                fragment: UsageFieldsFragmentDoc,
                fragmentName: 'UsageFields',
                id: 'PlanUsageType:{}',
                data: newUsage
              });
              if (socialProfile.__typename === 'InstagramSocialProfile') {
                if (socialProfile.apiType === InstagramApiType.FACEBOOK_GRAPH) {
                  // enable direct publishing for IG
                  setDirectPublishing(true, socialProfile, true);
                }
              }
            } else if (
              (!error && reconnected && connected && socialProfile) ||
              (error === 'SOCIAL_AUTH_WRONG_NETWORK' &&
                connected &&
                socialProfile &&
                socialProfile.__typename === 'InstagramSocialProfile')
            ) {
              setErrorModal({
                type: 'alreadyConnected',
                oAuthProvider: socialOAuth.oAuthProvider,
                network: socialOAuth.network,
                content: (
                  <>
                    You are trying to connect a Profile that&apos;s already connected to your SmarterQueue account
                    {socialProfile?.displayName ? `: ${socialProfile.displayName}` : ''}. If you wish to connect a
                    different Profile instead, please first log in with that Profile on{' '}
                    {capitalize(socialOAuth.network)} in this browser and try again.
                  </>
                ),
                profile: socialProfile
              });
            } else if (error) {
              const mappedError = getApolloError(error as ApolloErrorMapKeysType, 'connectMainSocialProfile');
              if (error === 'SOCIAL_NETWORK_NOT_AUTHORIZED') {
                const profileDisplayName = profilesHelpers.getDisplayProfileName(
                  socialOAuth.oAuthProvider,
                  socialOAuth.network
                );
                const detailMessage = `You cancelled the ${profileDisplayName} permissions step. No profiles have been connected.`;

                showToast({
                  severity: 'warn',
                  summary: `${profileDisplayName} Connection Cancelled`,
                  detail: detailMessage
                });
                return;
              }
              showToast({
                summary: `Profile not connected`,
                severity: 'error',
                detail: mappedError,
                sticky: true
              });
            }
          }
        );
      }
    } else {
      // error;
    }
  };

  const reconnectProfileAfterOAuth = async (socialOAuth: ISocialOAuth): Promise<void> => {
    if (socialOAuth.socialAuthToken && socialOAuth.profile) {
      const reconnectResult = await props
        .reconnectSocialProfileMutation({
          variables: {
            socialAuthToken: socialOAuth.socialAuthToken,
            profileId: socialOAuth.profile.id
          }
        })
        .finally(() => socialOAuthVar(null));
      if (reconnectResult.data) {
        let prevProfile: ReconnectSocialProfile_reconnectSocialProfile_socialProfile | null = null;
        // make sure we sort first additional reconnections since we need prevProfile before we enter main profile connection check
        const sortedProfiles = reconnectResult.data.reconnectSocialProfile.sort((a, b) => {
          if (a.additionalReconnection && !b.additionalReconnection) {
            return -1;
          } else if (!a.additionalReconnection && b.additionalReconnection) {
            return 1;
          }
          return 0;
        });
        sortedProfiles.forEach((profile: ReconnectSocialProfile_reconnectSocialProfile) => {
          const { connected, error, reconnected, socialProfile, additionalReconnection } = profile;
          if (additionalReconnection) {
            if (connected && reconnected && !error) {
              prevProfile = socialProfile;
            }
            return;
          }

          if (connected && !error && reconnected) {
            if (socialProfile?.__typename === 'FacebookSocialProfile' && socialProfile.type === 'group') {
              setLoading(true);
              props
                .appInstalledQuery({ variables: { id: socialProfile.id } })
                .then(appResult => {
                  setLoading(false);
                  const { data, error } = appResult;
                  if (
                    data?.socialProfile.__typename === 'FacebookSocialProfile' &&
                    data?.socialProfile.groupAppInstalled
                  ) {
                    setInfoModal({
                      type: 'GroupAdded',
                      profiles: [socialProfile]
                    });
                  }
                  if (error) {
                    throw error;
                    //TODO is should be temporary. once the issue is fixed we should remove. https://github.com/apollographql/apollo-client/issues/9142
                  }
                })
                .catch((e: ApolloError) => {
                  // TODO It doesn't catch it.
                  setLoading(false);
                  triggerErrorToast('App Installed Error')(e);
                });
            }
            showToast({
              summary: `Profile Re-connected`,
              severity: 'success',
              detail: `Social profile '${socialProfile?.displayName}' has been re-connected successfully.`
            });
          } else {
            if (!error) {
              // show generic error toast
              showToast({
                summary: `Profile Error`,
                severity: 'error',
                detail: GENERIC_ERROR_MESSAGE
              });
            } else {
              // If error is present and is profile mismatch, show dialog
              const trueProfile =
                socialProfile?.__typename === 'TwitterSocialProfile' ||
                socialProfile?.__typename === 'PinterestSocialProfile' ||
                (socialProfile?.__typename === 'LinkedInSocialProfile' && socialProfile.type === 'profile') ||
                (socialProfile?.__typename === 'InstagramSocialProfile' && socialProfile.apiType === 'BASIC_DISPLAY');
              if (error === 'SOCIAL_PROFILE_RECONNECT_MISMATCH' && trueProfile && prevProfile) {
                if (prevProfile.workspace.id === selectedWorkspaceId) {
                  setErrorModal({
                    type: 'reconnectedAnother',
                    oAuthProvider: socialOAuth.oAuthProvider,
                    network: socialOAuth.network,
                    content: (
                      <>
                        We re-connected your {capitalize(socialOAuth.network)} Profile{' '}
                        <strong>{prevProfile?.displayName ? `: ${prevProfile.displayName}` : ''}</strong>, since
                        that&apos;s the one you&apos;re currently logged in on this device. If you wish to re-connect
                        another profile instead, please first log in with that Profile on{' '}
                        {capitalize(socialOAuth.network)} in this browser and try again.
                      </>
                    ),
                    profile: socialOAuth.profile as GetSocialProfiles_workspace_socialProfiles
                  });
                } else {
                  const mappedError = getApolloError('NOT_FOUND', 'reconnectSocialProfile', {
                    type: profilesHelpers.getApolloSocialProfileTypeFromTypename(
                      socialProfile?.__typename as NonNullable<ReconnectSocialProfile_reconnectSocialProfile_socialProfile>['__typename']
                    )
                  });
                  showToast({
                    summary: `Profile Not Re-connected`,
                    severity: 'error',
                    detail: mappedError,
                    sticky: true
                  });
                }
              } else {
                if (error === 'SOCIAL_NETWORK_NOT_AUTHORIZED') {
                  const profileDisplayName = profilesHelpers.getDisplayProfileName(
                    socialOAuth.oAuthProvider,
                    socialOAuth.network
                  );
                  showToast({
                    severity: 'warn',
                    summary: `${profileDisplayName} Reconnection Cancelled`,
                    detail: `You cancelled the ${profileDisplayName} permissions step. No profiles have been re-connected.`
                  });
                  return;
                }
                // not reconnected we have an error so show it
                const mappedError = getApolloError(error as ApolloErrorMapKeysType, 'reconnectSocialProfile', {
                  type: profilesHelpers.getApolloSocialProfileTypeFromTypename(
                    socialProfile?.__typename as NonNullable<ReconnectSocialProfile_reconnectSocialProfile_socialProfile>['__typename']
                  )
                });
                showToast({
                  summary: `Profile Not Re-connected`,
                  severity: 'error',
                  detail: mappedError,
                  sticky: true
                });
              }
            }
          }
        });
      }
    } else {
      // error
      setLoading(false);
      showToast({
        summary: `Profile Not Re-connected`,
        severity: 'error',
        detail: GENERIC_ERROR_MESSAGE
      });
    }
  };

  const getListOfSecondaryProfilesAfterOAuth = (
    socialOAuth: ISocialOAuth,
    apolloErrorType: ApolloErrorMapTypesType
  ) => {
    let profileType: SecondaryProfileType | null = null;
    switch (socialOAuth.network) {
      case Network.LINKEDIN:
        profileType = SecondaryProfileType.COMPANY;
        break;
      case Network.GOOGLE:
        profileType = SecondaryProfileType.PAGE;
        break;
      case Network.INSTAGRAM:
        profileType = SecondaryProfileType.PAGE;
        break;
      case Network.FACEBOOK:
        if (socialOAuth.context === AuthContextEnum.connectPage) {
          profileType = SecondaryProfileType.PAGE;
        } else if (socialOAuth.context === AuthContextEnum.connectGroup) {
          profileType = SecondaryProfileType.GROUP;
        }
        break;
    }

    if (selectedOrganization && socialOAuth.socialAuthToken && socialOAuth.network && profileType) {
      props
        .getSecondaryProfiles({
          variables: {
            profileNetwork: socialOAuth.network,
            socialAuthToken: socialOAuth.socialAuthToken,
            type: profileType,
            workspaceId: selectedWorkspaceId
          }
        })
        .then(result => {
          const { data, error, loading: secondaryProfilesLoading } = result;

          if (loading && !secondaryProfilesLoading) {
            setLoading(false);
          }

          if (error) {
            throw error;
            //TODO is should be temporary. once the issue is fixed we should remove. https://github.com/apollographql/apollo-client/issues/9142
          }

          // Filter out profiles already linked to the selected organization.
          const filteredProfiles = data?.externalSecondaryProfiles.filter(
            value =>
              value.organizationId !== selectedOrganization.id ||
              (value.organizationId &&
                value.organizationId === selectedOrganization.id &&
                value.workspaceId !== selectedWorkspaceId)
          );

          if (filteredProfiles && filteredProfiles.length) {
            setSecondaryProfilesModal({
              type: profileType as SecondaryProfileType,
              // @ts-ignore
              profiles: filteredProfiles,
              organization: selectedOrganization,
              workspaceId: selectedWorkspaceId,
              socialOAuth
            });
          } else {
            setErrorModal({
              type: profileType as SecondaryProfileType,
              content: <>{getApolloError('NOT_FOUND', 'externalSecondaryProfiles', { type: apolloErrorType })}</>,
              oAuthProvider: socialOAuth.oAuthProvider,
              network: socialOAuth.network,
              profile: null
            });
          }
        })
        .catch(e => {
          setLoading(false);
          if (getApolloErrorMessage(e) === 'SOCIAL_NETWORK_NOT_AUTHORIZED') {
            const profileDisplayName = profilesHelpers.getDisplayProfileName(
              socialOAuth.oAuthProvider,
              socialOAuth.network
            );
            showToast({
              severity: 'warn',
              summary: `${profileDisplayName} Connection Cancelled`,
              detail: `You cancelled the ${profileDisplayName} permissions step. No profiles have been connected.`
            });
            return;
          }
          showToast({
            severity: 'error',
            summary: 'Profile Connection Error',
            detail: remapApolloErrors(e as ApolloError),
            sticky: true
          });
        })
        .finally(() => {
          setLoading(false);
          socialOAuthVar(null);
        });
    }
  };

  useEffect(() => {
    if (selectedWorkspaceId) {
      // Use 'network-only' to always get latest profile status from API in case cache is out of date.
      getSocialProfileQuery({ variables: { workspaceId: selectedWorkspaceId }, fetchPolicy: 'network-only' });
    }
  }, [selectedWorkspaceId, getSocialProfileQuery]);

  useEffect(() => {
    // we add getProfilesData as dependency too, in order to make sure it exists in cache before we read it
    if (
      selectedOrganization &&
      socialOAuth &&
      props.getProfilesData?.workspace?.socialProfiles &&
      !profilesQueryCalled
    ) {
      if (
        socialOAuth.network === Network.INSTAGRAM &&
        socialOAuth.oAuthProvider === SocialOAuthProvider.FACEBOOK &&
        socialOAuth.context === AuthContextEnum.connectProfile
      ) {
        getListOfSecondaryProfilesAfterOAuth(
          { ...socialOAuth, network: Network.INSTAGRAM },
          'INSTAGRAM_SOCIAL_PROFILE'
        );
      } else if (socialOAuth.context === AuthContextEnum.connectProfile) {
        connectProfileAfterOAuth(socialOAuth).catch(() => {});
      } else if (socialOAuth.context === AuthContextEnum.reconnectProfile) {
        reconnectProfileAfterOAuth(socialOAuth).catch(() => {});
      } else if (socialOAuth.context === AuthContextEnum.connectBusiness) {
        getListOfSecondaryProfilesAfterOAuth({ ...socialOAuth, network: Network.GOOGLE }, 'GOOGLE_SOCIAL_PROFILE');
      } else if (socialOAuth.context === AuthContextEnum.connectCompany) {
        getListOfSecondaryProfilesAfterOAuth({ ...socialOAuth, network: Network.LINKEDIN }, 'LINKED_IN_SOCIAL_PROFILE');
      } else if (
        socialOAuth.context === AuthContextEnum.connectPage ||
        socialOAuth.context === AuthContextEnum.connectGroup
      ) {
        getListOfSecondaryProfilesAfterOAuth({ ...socialOAuth, network: Network.FACEBOOK }, 'FACEBOOK_SOCIAL_PROFILE');
      }

      setProfilesQueryCalled(true);
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [
    socialOAuth,
    selectedWorkspaceId,
    selectedOrganization,
    props.getProfilesData?.workspace?.socialProfiles,
    profilesQueryCalled
  ]);

  useEffect(() => {
    if (props.getProfilesData && !socialOAuth && (!loadingUser || userData || errorUser)) {
      setLoading(false);
    }
  }, [props.getProfilesData, socialOAuth, loadingUser, userData, errorUser]);

  useEffect(() => {
    if (props.errorForRemapping) {
      if (getApolloErrorMessage(props.errorForRemapping) === 'PLAN_LIMIT_EXCEEDED') {
        getUsageFieldsLazyQuery().then(() => {
          setLimitReachedModalState({ isOpen: true, type: 'profiles' });
        });
      } else {
        // Moved show toast here in order to prevent useEffect in controller show same error multiple times
        showToast({
          severity: 'error',
          summary: 'Social Profiles Error',
          detail: remapApolloErrors(props.errorForRemapping),
          sticky: true
        });
      }
    }
  }, [props.errorForRemapping, setLimitReachedModalState, getUsageFieldsLazyQuery]);

  const mobileApps = useMemo(() => selectedOrganization?.mobileApps || [], [selectedOrganization]);
  const hasMobileAppConnected = useMemo(() => mobileApps.filter(app => app.connected).length > 0, [mobileApps]);

  const viewProps: ProfilesViewProps = {
    ...props,
    cancelConnect,
    connectLinkedinItems,
    directPublishingModal,
    errorModal,
    getListActionItems,
    infoModal,
    loading: props.loading || loading,
    reconnectProfile,
    reconnectProfileAfterOAuth,
    removeProfileModal,
    secondaryProfilesModal,
    setDirectPublishing,
    setDirectPublishingModal,
    setErrorModal,
    setInfoModal,
    setLoading,
    setRemoveProfileModal,
    setSecondaryProfilesModal,
    hasMobileAppConnected,
    showIgConnectModal: showIgConnectModal,
    setShowIgConnectModal: setShowIgConnectModal
  };
  return <ProfilesView {...viewProps} />;
};
